import { create } from 'zustand';
import type { Level, WordMeaning } from '@shared/types';
import type { PendingCardsStats } from '../api/workspaceApi';
import { generationFailureMessage } from '../lib/generationFailure';
import type {
  WorkspaceCard,
  WorkspaceCardProblem,
  WorkspaceCardReady,
  WorkspaceCardStatus,
  WorkspaceFilter,
} from './types';

interface WorkspaceState {
  cards: WorkspaceCard[];
  /** Модуль, чей список сейчас в сторе (2026-10-06). Ответ или карточка другого модуля
   *  в список не попадают: раньше мобильная очередь пустого модуля показала карточки
   *  предыдущего модуля до F5. */
  scope: string | null;
  filter: WorkspaceFilter;
  initialized: boolean;
  loading: boolean;
  stats: PendingCardsStats | null;
  /** Счётчик-тригер — увеличивается на каждое card-событие из SSE.
      Страницы listenят его как effect-dep и перезапрашивают данные. */
  refreshTick: number;

  setFilter: (f: WorkspaceFilter) => void;
  setLoading: (v: boolean) => void;
  setStats: (stats: PendingCardsStats | null) => void;
  bumpRefresh: () => void;
  hydrate: (cards: WorkspaceCard[]) => void;
  /** Открыт другой модуль — список очищается и дальше принимает только его карточки. */
  setScope: (moduleId: string | null) => void;
  replaceCards: (cards: WorkspaceCard[]) => void;
  add: (card: WorkspaceCard) => void;
  /**
   * Снимает строку по stateId (surrogate user_card_state.id), а не cardId.
   * После multi-state одна карточка может присутствовать как несколько строк
   * (по pos), поэтому удаление per-pos должно бить по конкретному stateId,
   * иначе удалятся ВСЕ строки этой карточки сразу.
   */
  remove: (stateId: string) => void;
  /** Сохранённые на сервере галочки смыслов строки: повторно открытая карточка
   *  (мобильный экран «Карточка готова») не должна показать прежний выбор. */
  setSessionSenses: (stateId: string, senseIds: string[]) => void;

  markReady: (cardId: string, ready: WorkspaceCardReady) => void;
  markProblem: (
    cardId: string,
    status: Extract<WorkspaceCardStatus, 'typo' | 'garbage' | 'error' | 'ai_error'>,
    problem: WorkspaceCardProblem,
  ) => void;
  markGenerating: (cardId: string) => void;

  applyCardEvent: (event: WorkspaceCardEvent) => void;

  reset: () => void;
}

export interface WorkspaceCardEventCardFields {
  transcription: string | null;
  cefr: string | null;
  primaryTranslation: string | null;
  meanings: WordMeaning[];
  status: string | null;
}

export interface WorkspaceCardEvent {
  type: 'succeeded' | 'expectedWord' | 'unExpectedError' | 'selectionFailed';
  cardId: string;
  suggestions: string[];
  errorInfo: string | null;
  card: WorkspaceCardEventCardFields | null;
}

function withCard(
  state: WorkspaceState,
  cardId: string,
  mut: (c: WorkspaceCard) => WorkspaceCard,
): Partial<WorkspaceState> {
  const idx = state.cards.findIndex((c) => c.cardId === cardId);
  if (idx === -1) return {};
  const next = state.cards.slice();
  next[idx] = mut(next[idx]);
  return { cards: next };
}

/** Строка, к которой относится событие генерации. У готовой карточки со строками
 *  изучаемых частей речи дописывание идёт в отдельной строке ожидания
 *  (meaningId === null) — событие должно менять её, а не первую строку карточки. */
function eventRowIndex(cards: WorkspaceCard[], event: WorkspaceCardEvent): number {
  const waiting = cards.findIndex((c) => c.cardId === event.cardId && c.meaningId === null);
  if (waiting !== -1 || event.type === 'succeeded' || event.type === 'selectionFailed') {
    // Строки ожидания нет, а строки карточки уже изучаются — их обновит рефетч.
    return waiting;
  }
  return cards.findIndex((c) => c.cardId === event.cardId);
}

const KNOWN_LEVELS: ReadonlyArray<Level> = ['A2', 'B1', 'B2', 'C1'];

function toLevel(value: string | null | undefined): Level | null {
  if (!value) return null;
  const upper = value.toUpperCase();
  return (KNOWN_LEVELS as readonly string[]).includes(upper) ? (upper as Level) : null;
}

function buildReadyFromEvent(
  card: WorkspaceCardEventCardFields,
  fallbackCefr: Level,
): WorkspaceCardReady {
  return {
    transcription: card.transcription ?? null,
    cefr: toLevel(card.cefr) ?? fallbackCefr,
    meanings: card.meanings ?? [],
  };
}

const inScope = (scope: string | null, card: WorkspaceCard) => !scope || card.payload.moduleId === scope;

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  cards: [],
  scope: null,
  filter: 'all',
  initialized: false,
  loading: false,
  stats: null,
  refreshTick: 0,

  setFilter: (f) => set({ filter: f }),
  setLoading: (v) => set({ loading: v }),
  setStats: (stats) => set({ stats }),
  bumpRefresh: () => set((s) => ({ refreshTick: s.refreshTick + 1 })),

  hydrate: (cards) =>
    set((state) => {
      const known = new Set(state.cards.map((c) => c.stateId));
      const incoming = cards.filter((c) => !known.has(c.stateId));
      return {
        cards: [...incoming, ...state.cards],
        initialized: true,
      };
    }),

  setScope: (moduleId) =>
    set((state) => (state.scope === moduleId ? {} : { scope: moduleId, cards: [], initialized: false })),

  replaceCards: (incoming) =>
    set((state) => {
      const cards = incoming.filter((c) => inScope(state.scope, c));
      // createdAt с сервера — время последнего изменения (touched_at). Если строка
      // уже была в стoре с более поздним временем (только что нажали «Создать»),
      // оставляем его, чтобы карточка не прыгала вниз до следующего ответа.
      // Ключ — stateId, иначе при N state'ах на одну cardId ключ не уникален.
      // Для оптимистично созданных строк (stateId='pending:<cardId>' /
      // 'manual:<cardId>') реальный stateId с бэка не совпадёт — для них
      // также пробуем фолбек по cardId.
      const byState = new Map(state.cards.map((c) => [c.stateId, c.createdAt]));
      const byCardOptimistic = new Map(
        state.cards
          .filter((c) => c.stateId.startsWith('pending:') || c.stateId.startsWith('manual:'))
          .map((c) => [c.cardId, c.createdAt]),
      );
      // Берём более позднее из двух: сервер знает время последнего изменения, а
      // оптимистичная строка — время нажатия в этой вкладке.
      const merged = cards.map((c) => {
        const prev = byState.get(c.stateId) ?? byCardOptimistic.get(c.cardId);
        return prev !== undefined && prev > c.createdAt ? { ...c, createdAt: prev } : c;
      });
      return { cards: merged, initialized: true };
    }),

  add: (card) =>
    set((state) => {
      if (!inScope(state.scope, card)) return state;
      if (state.cards.some((c) => c.stateId === card.stateId)) return state;
      return { cards: [card, ...state.cards] };
    }),

  remove: (stateId) =>
    set((state) => ({ cards: state.cards.filter((c) => c.stateId !== stateId) })),

  setSessionSenses: (stateId, senseIds) =>
    set((state) => {
      const idx = state.cards.findIndex((c) => c.stateId === stateId);
      if (idx === -1) return {};
      const next = state.cards.slice();
      next[idx] = { ...next[idx], sessionSenseIds: senseIds };
      return { cards: next };
    }),

  markReady: (cardId, ready) =>
    set((state) =>
      withCard(state, cardId, (c) => ({
        ...c,
        status: 'ready',
        ready,
        problem: undefined,
      })),
    ),

  markProblem: (cardId, status, problem) =>
    set((state) =>
      withCard(state, cardId, (c) => ({
        ...c,
        status,
        problem,
        ready: undefined,
      })),
    ),

  markGenerating: (cardId) =>
    set((state) =>
      withCard(state, cardId, (c) =>
        c.status === 'queued' ? { ...c, status: 'generating' } : c,
      ),
    ),

  applyCardEvent: (event) =>
    set((state) => {
      const idx = eventRowIndex(state.cards, event);
      if (idx === -1) return {};
      const next = state.cards.slice();
      const card = next[idx];
      if (event.type === 'selectionFailed') {
        // Выбранная часть речи не добавилась: бэк снял ожидание, строка «генерируется»
        // уходит. Уже готовую строку (частичный успех) обновит рефетч.
        if (card.status !== 'queued' && card.status !== 'generating') return {};
        next.splice(idx, 1);
        return { cards: next };
      }
      if (event.type === 'succeeded') {
        const nextPrimary = event.card?.primaryTranslation?.trim() ?? '';
        const fallbackPrimary = event.card?.meanings?.[0]?.senses?.[0]?.translations?.[0]?.text;
        const resolvedBack = nextPrimary || fallbackPrimary?.trim() || card.payload.back;
        // Шаг подтверждения убран: сервер авто-выбирает все части речи при генерации,
        // поэтому успешная карточка СРАЗУ готова к учёбе. Дальше bumpRefresh подтянет
        // настоящие per-pos state'ы (по одному на часть речи) с сервера.
        next[idx] = {
          ...card,
          status: 'ready',
          payload: { ...card.payload, back: resolvedBack },
          ready: event.card
            ? buildReadyFromEvent(event.card, card.payload.difficulty)
            : (card.ready ?? {
                transcription: null,
                cefr: card.payload.difficulty,
                meanings: [],
              }),
          problem: undefined,
        };
      } else if (event.type === 'expectedWord') {
        next[idx] = {
          ...card,
          status: 'typo',
          problem: {
            message: event.errorInfo ?? 'Похоже, в слове опечатка.',
            suggestions: event.suggestions,
          },
          ready: undefined,
        };
      } else if (event.type === 'unExpectedError') {
        const failureMessage = generationFailureMessage(event.card?.status, event.errorInfo);
        const isSystemFailure = failureMessage !== null;
        next[idx] = {
          ...card,
          status: isSystemFailure ? 'error' : 'garbage',
          problem: {
            message: failureMessage ?? event.errorInfo ?? 'Похоже, это не английское слово.',
            suggestions: [],
          },
          ready: undefined,
        };
      }
      return { cards: next };
    }),

  reset: () =>
    set({
      cards: [],
      scope: null,
      initialized: false,
      loading: false,
      // stats не сбрасываем — это per-module показатели; фильтр на них не влияет,
      // а смену модуля отдельно обрабатывает stats-эффект на странице.
    }),
}));
